import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useCompanyOptions } from '@/features/crm/api';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { useSaveProject } from './api';
import {
  BILLING_MODEL_LABELS,
  HEALTH_LABELS,
  PROJECT_STATUS_LABELS,
  PROJECT_TYPE_LABELS,
} from './labels';
import type { Project } from './types';

export function ProjectDialog({
  project,
  onClose,
  onSaved,
}: {
  project?: Project;
  onClose: () => void;
  onSaved?: (project: Project) => void;
}) {
  const save = useSaveProject();
  const { data: companies = [] } = useCompanyOptions();
  const { data: users = [] } = useUserOptions();

  const [form, setForm] = useState({
    name: project?.name ?? '',
    code: project?.code ?? '',
    description: project?.description ?? '',
    companyId: project?.companyId ?? '',
    projectType: project?.projectType ?? 'CUSTOM_DEVELOPMENT',
    billingModel: project?.billingModel ?? 'FIXED_PRICE',
    contractValue: project?.contractValue ?? '',
    budgetHours: project?.budgetHours ?? 0,
    budgetedCost: project?.budgetedCost ?? '',
    startDate: project?.startDate?.slice(0, 10) ?? '',
    targetEndDate: project?.targetEndDate?.slice(0, 10) ?? '',
    status: project?.status ?? 'PLANNING',
    health: project?.health ?? 'GREEN',
    managerId: project?.managerId ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{project ? `Edit ${project.name}` : 'New project'}</DialogTitle>
          <DialogDescription>
            The contract value and budget hours are what profitability is measured against, so
            they are worth getting right at the start.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Project name</Label>
              <Input value={form.name} onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Client</Label>
              <Select value={form.companyId} onChange={(e) => set({ companyId: e.target.value })}>
                <option value="">Choose a client</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Short code</Label>
            <Input
              value={form.code}
              placeholder="Left blank, it is taken from the name"
              onChange={(e) => set({ code: e.target.value.toUpperCase() })}
              maxLength={10}
              disabled={Boolean(project)}
            />
            <p className="text-xs text-muted-foreground">
              {project
                ? 'The code cannot change — every work item key is built from it.'
                : 'Work item keys are built from this, e.g. ACME-14.'}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select
                value={form.projectType}
                onChange={(e) => set({ projectType: e.target.value as never })}
              >
                {Object.entries(PROJECT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Billing model</Label>
              <Select
                value={form.billingModel}
                onChange={(e) => set({ billingModel: e.target.value as never })}
              >
                {Object.entries(BILLING_MODEL_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Contract value</Label>
              <Input
                inputMode="decimal"
                placeholder="0.00"
                value={form.contractValue}
                onChange={(e) => set({ contractValue: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Budget hours</Label>
              <Input
                type="number"
                min={0}
                value={form.budgetHours}
                onChange={(e) => set({ budgetHours: Number(e.target.value) })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Budgeted cost</Label>
              <Input
                inputMode="decimal"
                placeholder="0.00"
                value={form.budgetedCost}
                onChange={(e) => set({ budgetedCost: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <Input
                type="date"
                value={form.startDate}
                onChange={(e) => set({ startDate: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Target end date</Label>
              <Input
                type="date"
                value={form.targetEndDate}
                onChange={(e) => set({ targetEndDate: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Manager</Label>
              <Select value={form.managerId} onChange={(e) => set({ managerId: e.target.value })}>
                <option value="">Choose a manager</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onChange={(e) => set({ status: e.target.value as never })}>
                {Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Health</Label>
              <Select value={form.health} onChange={(e) => set({ health: e.target.value as never })}>
                {Object.entries(HEALTH_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || form.name.trim().length < 2 || !form.companyId}
            onClick={async () => {
              setError(null);
              try {
                const saved = await save.mutateAsync({
                  id: project?.id,
                  name: form.name,
                  code: form.code || undefined,
                  description: form.description || undefined,
                  companyId: form.companyId,
                  projectType: form.projectType,
                  billingModel: form.billingModel,
                  contractValue: form.contractValue || undefined,
                  budgetHours: form.budgetHours || undefined,
                  budgetedCost: form.budgetedCost || undefined,
                  startDate: form.startDate || undefined,
                  targetEndDate: form.targetEndDate || undefined,
                  status: form.status,
                  health: form.health,
                  managerId: form.managerId || undefined,
                });
                onSaved?.(saved);
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not save the project.',
                );
              }
            }}
          >
            {save.isPending ? 'Saving…' : project ? 'Save changes' : 'Create project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
